import React, { useState } from 'react';
import styled from 'styled-components';
import { Collapse } from 'react-collapse';
import { Flex, mq } from '@kvdbil/components';
import { ProcessedAuctionStates } from '~/helpers/auction';
import { ExplanationText } from '~/App/views/ObjectPage/components/ExplanationText';
import { inclVATtoExclVAT } from '~/helpers/currency';
import { useTranslation } from '~/Locale';
import { ProcessedAuction } from '~/App/shared/interfaces/ProcessedAuction';
import { PriceInEUR } from './PriceInEUR';
import { FINANCING_MINIMUM_AMOUNT } from '~/config/constants';
import { getAllowedResidualLoan } from '~/helpers/calculator';
import AlternativePaymentInformation from './AlternativePaymentInformation';
import { BuyMethodPriceLabelValue } from './BuyMethodPriceLabelValue';
import useShowExclVat from '~/App/shared/hooks/useShowExclVat';
import { useFeatureFlagValue } from '~/App/shared/featureFlags/hooks/useFeatureFlagValue';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 1rem;

  ${mq('laptop')} {
    border-bottom: 0px transparent;
  }
`;

export interface AuctionInfoProps {
  auction: ProcessedAuction;
  auctionStates: ProcessedAuctionStates;
  isRecreationalVehicle: boolean;
  isComingSoon: boolean;
  isHeavyEquipment: boolean;
}

const AuctionInfo = ({
  auction,
  isComingSoon,
  auctionStates,
  isRecreationalVehicle,
  isHeavyEquipment
}: AuctionInfoProps) => {
  const { t } = useTranslation();
  const [priceExplanationIsVisible, togglePriceExplanationVisibility] =
    useState(false);
  const heavyEquipmentHighlightExVat = useFeatureFlagValue(
    'heavy-equipment-highlight-ex-vat'
  );

  const { isLeading, isClosed } = auctionStates;
  const leadingBidAmount = auction?.leadingBid?.amount ?? 0;
  const startBid = auction.startBid;
  const hasNoBids = !leadingBidAmount || leadingBidAmount <= 0;
  const closedWithoutBids = isClosed && !auction.sellMethod && hasNoBids;

  const displayStartBid = leadingBidAmount === 0 && startBid > 0;
  const eligibleForAdditionalServices = auction.eligibleForAdditionalServices;

  const getTitle = (vatInfo: string) => {
    if (isComingSoon) {
      return auction.preliminaryPrice
        ? t('Preliminary price')
        : t('Preliminary price coming soon');
    } else if (displayStartBid && !isClosed) {
      return `${t('Start bid')} ${vatInfo}`;
    } else if (!isClosed && isLeading) {
      return `${t('Your leading bid')} ${vatInfo}`;
    } else if (isClosed && !isLeading) {
      return `${t('Closing bid')} ${vatInfo}`;
    } else if (isClosed && isLeading) {
      return `${t('Your winning bid')} ${vatInfo}`;
    }
    return `${t('Leading bid')} ${vatInfo}`;
  };

  const getBiddingValue = () => {
    let bidValue = leadingBidAmount;
    if (displayStartBid) {
      bidValue = startBid;
    }
    return bidValue;
  };

  const withResidualValue = getAllowedResidualLoan(auction.processObject);
  const biddingValue = getBiddingValue();

  const price = (isComingSoon ? auction.preliminaryPrice : biddingValue) ?? 0;

  const showExclVat = useShowExclVat(auction?.processObject);

  const showLoanInformation =
    !isRecreationalVehicle &&
    !isHeavyEquipment &&
    (leadingBidAmount || startBid) >= FINANCING_MINIMUM_AMOUNT &&
    leadingBidAmount <= 99999999 &&
    !(isComingSoon && !auction.preliminaryPrice);

  const showExplanationIcon = !isClosed && !isComingSoon && displayStartBid;

  return (
    <Container>
      <Flex gap={'0.5rem'} direction={'column'}>
        <div>
          <Flex gap={'0.5rem'} direction={'column'}>
            <BuyMethodPriceLabelValue
              price={
                heavyEquipmentHighlightExVat && showExclVat
                  ? inclVATtoExclVAT(price)
                  : price
              }
              currency={t('SEK')}
              title={
                heavyEquipmentHighlightExVat && showExclVat
                  ? getTitle(t('excl VAT'))
                  : getTitle(t('incl VAT'))
              }
              toggleExplanation={priceExplanationIsVisible}
              explanationIsVisible={() =>
                togglePriceExplanationVisibility(prev => !prev)
              }
              showExplanationIcon={showExplanationIcon}
              showEmptyMessage={closedWithoutBids}
              emptyMessage={t('No bids')}
              ariaControls="bidding-price-info-content"
            />

            {showExclVat && (
              <BuyMethodPriceLabelValue
                price={
                  heavyEquipmentHighlightExVat ? price : inclVATtoExclVAT(price)
                }
                currency={t('SEK')}
                title={
                  heavyEquipmentHighlightExVat
                    ? getTitle(t('incl VAT'))
                    : getTitle(t('excl VAT'))
                }
                smallValue
              />
            )}
          </Flex>

          {showExplanationIcon && (
            <Collapse isOpened={priceExplanationIsVisible}>
              <ExplanationText id="bidding-price-info-content">
                {t('Starting price')}
              </ExplanationText>
            </Collapse>
          )}
        </div>

        <PriceInEUR
          title={t('Leading bid in EUR')}
          price={auction?.activeAuction?.highestBid?.eurAmount}
        />

        {eligibleForAdditionalServices && !closedWithoutBids && (
          <AlternativePaymentInformation
            withResidualValue={withResidualValue}
            auctionCost={leadingBidAmount || startBid}
            showLoanInformation={showLoanInformation}
          />
        )}
      </Flex>
    </Container>
  );
};

export default AuctionInfo;
